import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { AdminHeader, AdminSidebar } from "../components/AdminHeader";
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  Send,
  User,
  Mail,
  FileText,
  Download,
  Phone,
  RefreshCw,
} from "lucide-react";

const AdminProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, logout, api } = useAuth();
  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, formRes] = await Promise.all([
        api.get(`/admin/projects/${projectId}`),
        api.get(`/admin/projects/${projectId}/form`),
      ]);
      setProject(projectRes.data);
      setForm(formRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Projektdaten");
      navigate("/admin");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvitation = async () => {
    try {
      await api.post(`/admin/projects/${projectId}/resend-invitation`);
      toast.success("Einladung erneut versendet");
    } catch (error) {
      toast.error("Fehler beim Versenden der Einladung");
    }
  };

  const handleReopenProject = async () => {
    try {
      await api.post(`/admin/projects/${projectId}/reopen`);
      toast.success("Projekt zur Bearbeitung freigegeben");
      setReopenDialogOpen(false);
      fetchProjectData();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Freigeben";
      toast.error(message);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Anwahlfolgen_${project.name.replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Fehler beim Download des PDFs");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    if (status === "submitted") {
      return (
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Abgeschlossen
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-50 text-amber-700 border-amber-200">
        <Clock className="w-3 h-3 mr-1" />
        Ausstehend
      </Badge>
    );
  };

  const isSuperAdmin = user?.role === "superadmin";

  if (isLoading) {
    return (
      <div className="page-bg min-h-screen">
        <AdminSidebar />
        <div className="ml-56 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
        </div>
      </div>
    );
  }

  const filledParticipants = form?.participants?.filter((p) => p.name || p.phone) || [];
  const filledSequences = form?.sequences?.filter((s) => s.message_type) || [];

  return (
    <div className="page-bg min-h-screen">
      <AdminSidebar />
      <div className="ml-56">
        <AdminHeader />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button & Title */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="text-muted-foreground hover:text-foreground"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="font-manrope font-bold text-2xl text-foreground">
                    {project?.name}
                  </h1>
                  {getStatusBadge(project?.status)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {project?.status === "pending" && (
                <Button
                  variant="outline"
                  onClick={handleResendInvitation}
                  data-testid="resend-invitation-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Erneut einladen
                </Button>
              )}
              {project?.status === "submitted" && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setReopenDialogOpen(true)}
                    data-testid="reopen-project-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Zur Bearbeitung freigeben
                  </Button>
                  <Button
                    onClick={handleDownloadPdf}
                  className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF herunterladen
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Project Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="card-base">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Projekt</p>
                  <p className="font-medium text-slate-900">{project?.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Kunde</p>
                  <p className="font-medium text-slate-900">{project?.customer_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">E-Mail</p>
                  <p className="font-medium text-slate-900">{project?.customer_email}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timestamps */}
        <Card className="card-base mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Erstellt am</p>
                <p className="font-medium text-slate-900">{formatDate(project?.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Abgeschlossen am</p>
                <p className="font-medium text-slate-900">
                  {project?.status === "submitted" ? formatDate(project?.submitted_at) : "-"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Data */}
        {form && (
          <>
            {/* Participants */}
            <Card className="card-base mb-6">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="font-manrope text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-slate-600" />
                  Teilnehmer ({filledParticipants.length}/10)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {filledParticipants.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-16 text-center">Nr.</TableHead>
                          <TableHead>Typ</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Rufnummer</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filledParticipants.map((participant) => (
                          <TableRow key={participant.number}>
                            <TableCell className="text-center font-mono font-medium">
                              {participant.number}
                            </TableCell>
                            <TableCell>{participant.type || "-"}</TableCell>
                            <TableCell>{participant.name || "-"}</TableCell>
                            <TableCell className="font-mono">
                              {participant.phone ? `${participant.country_code || ''} ${participant.phone}` : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    Keine Teilnehmer angelegt
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sequences */}
            <Card className="card-base mb-6">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="font-manrope text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-slate-600" />
                  Anwahlfolgen ({filledSequences.length}/8)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {filledSequences.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-16 text-center">Linie</TableHead>
                          <TableHead>Meldung</TableHead>
                          <TableHead>Teilnehmer-Zuordnung</TableHead>
                          <TableHead className="w-20 text-center">Zyklen</TableHead>
                          <TableHead className="w-20 text-center">Zeit (s)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filledSequences.map((sequence) => (
                          <TableRow key={sequence.line}>
                            <TableCell className="text-center font-mono font-medium">
                              {sequence.line}
                            </TableCell>
                            <TableCell>
                              {sequence.message_type || "-"}
                              {sequence.message_type === "Testmeldung" && (
                                <span className="ml-2 text-xs text-slate-500">
                                  (täglich 13:00 Uhr)
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {sequence.assignments
                                ?.filter((a) => a.value && a.value !== "keine Teilnehmer")
                                .map((a) => a.value)
                                .join(" → ") || "-"}
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {sequence.cycle_count || "-"}
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {sequence.cycle_time || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    Keine Anwahlfolgen konfiguriert
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Signature */}
            {form.signature && (
              <Card className="card-base">
                <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                  <CardTitle className="font-manrope text-lg">Unterschrift</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-start gap-6">
                    <div className="bg-white border border-slate-200 rounded-lg p-4">
                      <img
                        src={form.signature}
                        alt="Unterschrift"
                        className="max-w-[300px] h-auto"
                      />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Bestätigt von</p>
                      <p className="font-medium text-slate-900">{form.customer_name}</p>
                      <p className="text-sm text-slate-500 mt-3 mb-1">Datum</p>
                      <p className="font-medium text-slate-900">{form.date}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Reopen Dialog */}
        <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Projekt zur Bearbeitung freigeben</DialogTitle>
              <DialogDescription>
                Sind Sie sicher? Der Kunde kann das Formular dann erneut bearbeiten und muss es nochmals bestätigen.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReopenDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleReopenProject}
                className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Freigeben
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      </div>
    </div>
  );
};

export default AdminProjectDetail;
